import {
  AUTOMATION_DEFAULT_TIME_ZONE,
  AutomationTemplateIds,
  allAutomationWeekdays,
  type Automation,
  type AutomationMonthlySchedule,
  type AutomationTrigger,
  type AutomationWeekday,
} from "@repo/app-types/entities/automation";
import { formatTimeForDisplay } from "@repo/app-types/entities/schedulingConfiguration";
import { assertUnreachable } from "@repo/app-types/helpers/utilities";
import { DateTime, type WeekdayNumbers } from "luxon";

export const weekdayLabels: Record<AutomationWeekday, string> = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

/** Humanizes a monthly schedule, e.g. "The first day", "The last day". */
export function describeMonthlySchedule(
  schedule: AutomationMonthlySchedule,
): string {
  switch (schedule.kind) {
    case "first_day_of_month":
      return "The first day";
    case "first_weekday_of_month":
      return `The first ${weekdayLabels[schedule.weekday]}`;
    case "last_day_of_month":
      return "The last day";
    default:
      return assertUnreachable(schedule);
  }
}

/** Joins weekday labels in week order, e.g. "Monday, Wednesday & Friday". */
function describeWeekdays(weekdays: readonly AutomationWeekday[]): string {
  const labels = allAutomationWeekdays
    .filter((weekday) => weekdays.includes(weekday))
    .map((weekday) => weekdayLabels[weekday]);
  if (labels.length <= 1) {
    return labels[0] ?? "";
  }
  return `${labels.slice(0, -1).join(", ")} & ${labels[labels.length - 1]}`;
}

/** Humanizes a trigger, e.g. "Monday & Wednesday at 9:00 AM". */
export function describeTrigger(trigger: AutomationTrigger): string {
  if (trigger.frequency === "hourly") {
    return "Hourly";
  }
  const time = formatTimeForDisplay(trigger.time_of_day);
  switch (trigger.frequency) {
    case "daily":
      return `Daily at ${time}`;
    case "weekly":
      return `${describeWeekdays(trigger.weekdays)} at ${time}`;
    case "monthly":
      return `${describeMonthlySchedule(trigger.schedule)} at ${time}`;
    default:
      return assertUnreachable(trigger);
  }
}

/**
 * Humanizes when an automation runs, for the Trigger column of the agents
 * table, e.g. "Monday & Friday at 8am", "Tuesday at 9am", "Daily".
 */
export function describeAutomationTrigger(
  automation: Pick<Automation, "template_id" | "settings">,
): string {
  const { settings } = automation;
  switch (automation.template_id) {
    case AutomationTemplateIds.TEAMMATE_COACHING:
      // Coaching goals go out on a fixed schedule (see the template
      // description); it isn't configurable per agent.
      return "Monday & Friday at 8am";
    case AutomationTemplateIds.PLAYBOOK_SCORE_MOVEMENT: {
      if (!("trigger" in settings)) {
        return "Weekly";
      }
      return describeTrigger(settings.trigger);
    }
    case AutomationTemplateIds.LEAD_BURNING: {
      if (!("trigger" in settings)) {
        return "Daily";
      }
      return settings.trigger.frequency === "hourly" ? "Hourly" : "Daily";
    }
    case AutomationTemplateIds.CUSTOMER_PRAISE_DIGEST:
    case AutomationTemplateIds.PERFECT_CALLS:
    case AutomationTemplateIds.CUSTOM: {
      if (!("trigger" in settings)) {
        return "Daily";
      }
      return describeTrigger(settings.trigger);
    }
    default:
      assertUnreachable(automation.template_id);
  }
}

/** Luxon weekday numbers (1 = Monday … 7 = Sunday). */
const weekdayToLuxon: Record<AutomationWeekday, WeekdayNumbers> = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 7,
};

/**
 * The agent's next scheduled run for the info popover. We compute a concrete
 * datetime for Playbook score movement only, matching its pre-unification
 * behavior; the other templates keep their humanized cadence, and disabled
 * agents have no next run. The trigger's `time_of_day` is a wall-clock time in
 * AUTOMATION_DEFAULT_TIME_ZONE — the zone the scheduler builds its crons in —
 * so we evaluate `now` in that same zone rather than the viewer's local one; a
 * Pacific admin viewing a 09:00 ET automation then sees the real 09:00 ET run,
 * tagged with the zone abbreviation.
 */
export function describeAutomationNextRun(
  automation: Pick<Automation, "template_id" | "settings" | "is_enabled">,
  now: DateTime = DateTime.local(),
): string {
  if (!automation.is_enabled) {
    return "N/A";
  }

  const { settings } = automation;
  if (
    automation.template_id === AutomationTemplateIds.PLAYBOOK_SCORE_MOVEMENT &&
    "trigger" in settings &&
    // The two frequencies score movement's form offers; the rest of the shared
    // trigger union has no run day to project forward from.
    (settings.trigger.frequency === "weekly" ||
      settings.trigger.frequency === "monthly")
  ) {
    const { trigger } = settings;
    const [hourStr = "0", minuteStr = "0"] = trigger.time_of_day.split(":");
    const time = { hour: Number(hourStr), minute: Number(minuteStr) };
    const zonedNow = now.setZone(AUTOMATION_DEFAULT_TIME_ZONE);
    const next =
      trigger.frequency === "monthly"
        ? computeNextMonthlyRun(zonedNow, trigger.schedule, time)
        : computeNextWeeklyRun(zonedNow, trigger.weekdays, time);
    // ZZZZ appends the zone abbreviation ("EST"/"EDT") so the displayed time
    // reads unambiguously in the scheduler's zone.
    return next.toFormat("MMM d, h:mm a ZZZZ");
  }

  return describeAutomationTrigger(automation);
}

interface TimeOfDay {
  hour: number;
  minute: number;
}

/**
 * The soonest next run across the trigger's weekdays, at or after `now`. A
 * weekly trigger may name several days, so each is projected and the earliest
 * wins.
 */
function computeNextWeeklyRun(
  now: DateTime,
  weekdays: readonly AutomationWeekday[],
  time: TimeOfDay,
): DateTime {
  const runs = weekdays.map((weekday) => {
    const next = now.set({
      weekday: weekdayToLuxon[weekday],
      hour: time.hour,
      minute: time.minute,
      second: 0,
      millisecond: 0,
    });
    return next <= now ? next.plus({ weeks: 1 }) : next;
  });
  return runs.reduce((soonest, run) => (run < soonest ? run : soonest));
}

/** The next monthly run at the given time, at or after `now`. */
function computeNextMonthlyRun(
  now: DateTime,
  schedule: AutomationMonthlySchedule,
  time: TimeOfDay,
): DateTime {
  const thisMonthRun = resolveMonthlyRun(
    now.set({ day: 1, hour: time.hour, minute: time.minute }),
    schedule,
  );
  if (thisMonthRun > now) {
    return thisMonthRun;
  }
  return resolveMonthlyRun(
    now
      .plus({ months: 1 })
      .set({ day: 1, hour: time.hour, minute: time.minute }),
    schedule,
  );
}

/**
 * Resolves the concrete run datetime within the month of `monthStart` (which
 * must already be day 1 at the target time) for the given monthly schedule.
 */
function resolveMonthlyRun(
  monthStart: DateTime,
  schedule: AutomationMonthlySchedule,
): DateTime {
  const base = monthStart.set({ second: 0, millisecond: 0 });
  switch (schedule.kind) {
    case "first_day_of_month":
      return base;
    case "last_day_of_month":
      // Day 1 of the next month minus one day — the month's last day at the
      // configured time, without Luxon's invalid-date-only `daysInMonth`
      // undefined branch.
      return base.plus({ months: 1 }).minus({ days: 1 });
    case "first_weekday_of_month": {
      // Luxon weekday: 1 = Monday … 7 = Sunday. Days to add to reach the first
      // occurrence of the target weekday within the month.
      const offset = (weekdayToLuxon[schedule.weekday] - base.weekday + 7) % 7;
      return base.plus({ days: offset });
    }
    default:
      return assertUnreachable(schedule);
  }
}

/** Formats how long a run took, e.g. "45 sec" or "3 min". */
export function formatRunDuration(
  startedAt: string,
  finishedAt: string,
): string {
  const duration = DateTime.fromISO(finishedAt).diff(
    DateTime.fromISO(startedAt),
  );
  const seconds = Math.max(0, Math.round(duration.as("seconds")));
  if (seconds < 60) {
    return `${seconds} sec`;
  }
  return `${Math.round(seconds / 60)} min`;
}
